import React, { useState } from "react";
import { View, StyleSheet } from "react-native";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import { CheckIcon, CopyIcon, UsersIcon, ArrowClockwiseIcon, PlusCircleIcon, TrashIcon } from "@phosphor-icons/react";
import { useAppConfig } from "../../../AppConfigProvider";
import { EnrollmentLinkData } from "../../../graphql/generated/graphql";
import { formatSubmittedDate } from "../../../utils/date";

interface EnrollmentLinkManagerProps {
  handleCreateLink: () => void;
  handleUpdateLink: () => void;
  handleDeleteLink: () => void;
  enrollmentLink: EnrollmentLinkData | null;
  isLoading: boolean;
}

const EnrollmentLinkManager: React.FC<EnrollmentLinkManagerProps> = ({
  enrollmentLink,
  handleCreateLink,
  handleUpdateLink,
  handleDeleteLink,
  isLoading,
}) => {
  const [isLinkCopied, setIsLinkCopied] = useState(false);
  const appConfig = useAppConfig();
  const primaryColor = appConfig.primaryColor;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(enrollmentLink?.enrollmentLink);
    setIsLinkCopied(true);
    setTimeout(() => setIsLinkCopied(false), 1000);
  };

  if (isLoading) {
    return (
      <View style={styles.container}>
        <CustomText text="Loading enrollment link status..." size="m" />
      </View>
    );
  }

  if (!enrollmentLink) {
    return (
      <View style={styles.container}>
        <CustomText text="No enrollment link created yet" size="m" weight="medium" style={styles.title} />
        <CustomSpacing type="vertical" size="s" />
        <CustomText
          text="Create an enrollment link to allow students to join this course"
          size="s"
          style={styles.description}
        />
        <CustomSpacing type="vertical" size="m" />
        <CustomButton
          text="Generate Enrollment Link"
          styleType="primaryLight"
          leftIcon={<PlusCircleIcon size={16} color={primaryColor} />}
          size="m"
          onPress={handleCreateLink}
          style={styles.addButton}
        />
      </View>
    );
  }

  const now = new Date();
  const expiresAt = new Date(enrollmentLink.expiresAt);
  const isExpired = expiresAt < now;
  const isFull = enrollmentLink.maxEnrollments
    ? enrollmentLink.currentEnrollments >= enrollmentLink.maxEnrollments
    : false;

  const titleText =
    enrollmentLink.expiresAt && isExpired
      ? "Enrollment Link Expired"
      : isFull
        ? "Enrollment Link Full"
        : "Enrollment Link Active";
  const badgeText = enrollmentLink.expiresAt && isExpired ? "EXPIRED" : isFull ? "FULL" : "ACTIVE";
  const badgeColor = enrollmentLink.expiresAt && isExpired ? "#FEE2E2" : isFull ? "#FEF3C7" : "#D1FAE5";
  const badgeTextColor = enrollmentLink.expiresAt && isExpired ? "#DC2626" : isFull ? "#F59E0B" : "#059669";

  if (enrollmentLink) {
    return (
      <View style={styles.container}>
        <View style={styles.statusContainer}>
          <View style={styles.statusHeader}>
            <CustomText text={titleText} size="m" weight="medium" style={styles.title} />
            <View style={[styles.badge, { backgroundColor: badgeColor }]}>
              <CustomText
                text={badgeText}
                size="xs"
                weight="medium"
                useTranslationText={false}
                style={[styles.badgeText, { color: badgeTextColor }]}
              />
            </View>
          </View>
          <CustomSpacing type="vertical" size="s" />

          {enrollmentLink.maxEnrollments ? (
            <CustomText
              text="enrollmentLink.maxAndCurrentEnrollments"
              interpolation={{
                currentEnrollments: enrollmentLink.currentEnrollments,
                maxEnrollments: enrollmentLink.maxEnrollments,
              }}
              size="s"
              style={styles.description}
            />
          ) : (
            <CustomText
              text="enrollmentLink.currentEnrollments"
              interpolation={{
                currentEnrollments: enrollmentLink.currentEnrollments,
              }}
              size="s"
              style={styles.description}
            />
          )}
          <CustomSpacing type="vertical" size="s" />
          <CustomText
            text={enrollmentLink.expiresAt ? "enrollmentLink.expiresAt" : "enrollmentLink.neverExpires"}
            interpolation={enrollmentLink.expiresAt ? { expiresAt: formatSubmittedDate(enrollmentLink.expiresAt) } : {}}
            size="s"
            style={styles.description}
          />
        </View>
        <CustomSpacing type="vertical" size="m" />
        <View style={styles.buttonContainer}>
          {(!enrollmentLink.expiresAt || !isExpired) && !isFull && (
            <CustomButton
              text={isLinkCopied ? "Link copied!" : "Copy Link"}
              styleType="primaryLight"
              size="m"
              leftIcon={<CopyIcon color={primaryColor} size={16} />}
              onPress={handleCopyLink}
              style={styles.actionButton}
            />
          )}
          <CustomButton
            text="Edit Link"
            styleType="primaryLight"
            size="m"
            leftIcon={<ArrowClockwiseIcon color={primaryColor} size={16} />}
            onPress={handleUpdateLink}
            style={styles.actionButton}
          />
          <CustomButton
            text="Delete Link"
            styleType="primaryLight"
            size="m"
            leftIcon={<TrashIcon color={primaryColor} size={16} />}
            onPress={handleDeleteLink}
            style={styles.actionButton}
          />
        </View>
      </View>
    );
  }

  return null;
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    padding: 16,
  },
  title: {
    color: "#1F2937",
  },
  description: {
    color: "#6B7280",
  },
  statusContainer: {
    marginBottom: 8,
  },
  statusHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  buttonContainer: {
    flexDirection: "row",
    gap: 12,
  },
  addButton: {
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: "#3B82F6",
    alignSelf: "stretch",
    alignItems: "center",
    justifyContent: "center",
  },
  actionButton: {
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: "#3B82F6",
    alignSelf: "stretch",
    alignItems: "center",
    justifyContent: "center",
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
  },
  badgeText: {
    color: "#065F46",
    fontSize: 10,
    letterSpacing: 0.5,
  },
});

export default EnrollmentLinkManager;
